import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router';
import { useSelector } from 'react-redux';
import { Skeleton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { RootState } from 'store';
import { useSortPreference } from 'hooks/useSortPreference';
import { useFetchPublicHaveListQuery } from 'store/services/collection/publicCollectionApi';
import { useGetPublicProfileQuery, useUpdateProfileSettingsMutation } from 'store/services/profile/profileApi';
import { useLazyGetReceiveCandidatesQuery } from 'store/services/tradeOffers/tradeOffersApi';
import { writeOfferBuilderSeed } from 'utils/offerBuilderSeed';
import { parseOfferError } from 'utils/offerErrors';
import LoginPromptModal from 'components/LoginPromptModal';
import EnableTradeOffersModal from 'components/EnableTradeOffersModal';
import { CollectionList } from 'types/interfaces/collectionList';
import { MTGCard } from 'types/interfaces/mtgCard';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { SortOrder } from 'types/interfaces/sort';
import { Points } from 'components/Points/Points';
import MagicButton from 'components/MagicButton/MagicButton';
import TextInputField from 'components/TextInputField';
import CardsTable from 'components/CardsTable';
import { CardsTableCellsData } from 'components/CardsTable/types';
import { SetIcon } from 'components/SetIcon';
import CardSizeSelector from 'components/CardSizeSelector/CardSizeSelector';
import { FoilCardImage } from 'components/FoilCardImage';
import { AssetUrls } from 'config/assets';
import { getCardImageUrl, SORT_OPTIONS, VIEW_OPTIONS } from 'utils/wantListHelpers';
import WantListCardOverlay from '../ProfileWantListTab/WantListCardOverlay';
import './ProfileHaveListTab.css';

interface ProfileHaveListTabProps {
  username: string;
  isOwnProfile?: boolean;
}

// Format number with commas
const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

// Chevron SVG used in dropdowns
const ChevronSvg: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`profile-have-dropdown-chevron ${open ? 'open' : ''}`}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path d="M3 4.5L6 7.5L9 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Condition abbreviation map
const CONDITION_MAP: Record<string, string> = {
  'NEAR_MINT': 'NM',
  'SLIGHTLY_PLAYED': 'LP',
  'MODERATELY_PLAYED': 'MP',
  'HEAVILY_PLAYED': 'HP',
  'DAMAGED': 'DMG',
};

// Card item component
const HaveListCardItem: React.FC<{
  item: CollectionList;
  isOwnProfile: boolean;
  /**
   * True only when the holder has EXPLICITLY turned off incoming trade offers.
   * Fail open: an undefined flag leaves the affordance visible (see the caller).
   */
  offersDisabled?: boolean;
  onAddToTrade?: (item: CollectionList) => void;
}> = ({ item, isOwnProfile, offersDisabled = false, onAddToTrade }) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileTapped, setIsMobileTapped] = useState(false);
  const defaultImage = `${AssetUrls.base}/assets/images/card-back.png`;
  const imageUrl = getCardImageUrl(item);

  const cardName = item.card?.name || 'Unknown Card';

  // Self-view gets the "Manage" hover overlay (→ /my-cards); other profiles get
  // the "Add to Trade" overlay mirroring the want list affordance exactly — it
  // seeds the offer builder with this card in the They send column.
  const showOverlay = isHovered || isMobileTapped;

  const handleClick = (e: React.MouseEvent) => {
    // The card body doesn't navigate when the overlay is available — use the
    // arrow / card-name link. On mobile, first tap reveals the overlay.
    if (window.innerWidth <= 768 && !isMobileTapped) {
      e.preventDefault();
      e.stopPropagation();
      setIsMobileTapped(true);
      return;
    }
    if (isOwnProfile) return;
    if (item.card) {
      navigate(buildCardUrl(item.card));
    }
  };

  const handleViewCard = () => {
    if (item.card) {
      navigate(buildCardUrl(item.card));
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsMobileTapped(false);
  };

  const handleOverlayClose = () => {
    setIsMobileTapped(false);
    setIsHovered(false);
  };

  return (
    <div
      className="profile-have-card-item"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="profile-have-card-image-container">
        {/* The canonical foil treatment (same component as /my-cards and the
            trade tiles) — foil reads from the art, so no FOIL text tag */}
        <FoilCardImage
          src={imageError ? defaultImage : imageUrl}
          alt={cardName}
          foil={!!item.foil}
          className="profile-have-card-image-wrap"
          onError={() => setImageError(true)}
          loading="lazy"
        />
        {item.quantity > 1 && !showOverlay && (
          <div className="profile-have-card-quantity">x{item.quantity}</div>
        )}
        {/* The tag slot carries what the art can't show: condition */}
        {item.condition && !showOverlay && (
          <div className="profile-have-card-condition">
            {CONDITION_MAP[item.condition] || item.condition}
          </div>
        )}

        {showOverlay && (
          <WantListCardOverlay
            cardName={cardName}
            quantity={item.quantity}
            addedCount={0}
            onAddToTrade={() => onAddToTrade?.(item)}
            onViewCard={handleViewCard}
            onClose={isMobileTapped ? handleOverlayClose : undefined}
            isMobile={isMobileTapped}
            mode={isOwnProfile ? 'manage' : offersDisabled ? 'view' : 'trade'}
            manageHref="/my-cards/have-list"
          />
        )}
      </div>
      <div className="profile-have-card-info">
        <span className="profile-have-card-name" title={cardName}>
          {cardName}
        </span>
        {item.displayPrice != null && (
          <Points points={item.displayPrice} type="small" />
        )}
      </div>
    </div>
  );
};

// Loading skeleton
const HaveListSkeleton: React.FC = () => (
  <div className="profile-have-skeleton">
    <div className="profile-have-header">
      <Skeleton variant="text" width={120} height={28} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
    </div>
    <div className="profile-have-grid">
      {[...Array(12)].map((_, i) => (
        <div key={i} className="profile-have-card-item">
          <Skeleton
            variant="rectangular"
            width="100%"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.1)',
              aspectRatio: '488 / 680',
              borderRadius: '8px',
            }}
          />
          <Skeleton variant="text" width="80%" height={16} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 1 }} />
        </div>
      ))}
    </div>
  </div>
);

// Private have list message
interface PrivateHaveListMessageProps {
  isOwnProfile?: boolean;
  onEnabled: () => void;
}

const PrivateHaveListMessage: React.FC<PrivateHaveListMessageProps> = ({ isOwnProfile, onEnabled }) => {
  const [updateProfileSettings, { isLoading: isEnabling }] = useUpdateProfileSettingsMutation();

  const handleEnable = async () => {
    try {
      await updateProfileSettings({ showHaveListOnProfile: true }).unwrap();
      toast.success('Have list is now visible on your profile');
      onEnabled();
    } catch (error) {
      console.error('Failed to update profile settings:', error);
      toast.error('Failed to update profile settings. Please try again.');
    }
  };

  return (
    <div className="profile-have-private">
      <div className="profile-have-private-icon">&#128274;</div>
      <h3 className="profile-have-private-title">Have List is Private</h3>
      {isOwnProfile ? (
        <>
          <p className="profile-have-private-description">
            Your have list is private. Show it on your profile so others can see what you have to trade.
          </p>
          <button
            type="button"
            className="profile-have-private-enable-button"
            onClick={handleEnable}
            disabled={isEnabling}
          >
            {isEnabling ? 'Showing…' : 'Show My Have List'}
          </button>
          <Link to="/settings" className="profile-have-private-secondary-link">
            or manage in Settings
          </Link>
        </>
      ) : (
        <p className="profile-have-private-description">
          Have lists on TradeMagic are private by default. This user hasn&apos;t made theirs public.
        </p>
      )}
    </div>
  );
};

export const ProfileHaveListTab: React.FC<ProfileHaveListTabProps> = ({ username, isOwnProfile }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [previousPages, setPreviousPages] = useState<CollectionList[]>([]);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Sort state - persisted to URL params and localStorage
  const { orderBy: sortOrder, setOrderBy: setSortOrder } = useSortPreference('profileHaveListSort', 'created_at', 'desc');

  // View mode state - persist to sessionStorage
  const [viewMode, setViewMode] = useState<'images' | 'list'>(() => {
    const saved = sessionStorage.getItem('profileHaveListViewMode');
    return (saved as 'images' | 'list') || 'images';
  });

  // Image size state - persist to localStorage
  type ImageSize = 'S' | 'M' | 'L';
  const [imageSize, setImageSize] = useState<ImageSize>(() => {
    const saved = localStorage.getItem('profileHaveListImageSize');
    return (saved as ImageSize) || 'S';
  });

  const handleImageSizeChange = useCallback((size: ImageSize) => {
    setImageSize(size);
    localStorage.setItem('profileHaveListImageSize', size);
  }, []);

  // Dropdown states
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);

  const sortRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<HTMLDivElement>(null);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Debounce search term (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Synchronous pagination reset during render (prevents stale data flash)
  const prevFilterDepsRef = useRef({
    search: '',
    sort: 'created_at' as string | undefined,
    direction: 'desc' as string | undefined,
  });

  const filterDepsChanged =
    prevFilterDepsRef.current.search !== debouncedSearchTerm ||
    prevFilterDepsRef.current.sort !== sortOrder.sort ||
    prevFilterDepsRef.current.direction !== sortOrder.direction;

  if (filterDepsChanged) {
    setPage(1);
    setPreviousPages([]);
    prevFilterDepsRef.current = {
      search: debouncedSearchTerm,
      sort: sortOrder.sort,
      direction: sortOrder.direction,
    };
  }

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setIsSortOpen(false);
      }
      if (viewRef.current && !viewRef.current.contains(event.target as Node)) {
        setIsViewOpen(false);
      }
    };
    if (isSortOpen || isViewOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSortOpen, isViewOpen]);

  // Sort handlers
  const currentSortValue = `${sortOrder.sort}-${sortOrder.direction}`;
  const currentSortLabel = SORT_OPTIONS.find(o => o.value === currentSortValue)?.label || 'Date Added';

  const handleSortSelect = useCallback((compoundValue: string) => {
    const [sort, direction] = compoundValue.split('-');
    setSortOrder({ sort, direction: direction as SortOrder });
    setIsSortOpen(false);
  }, [setSortOrder]);

  // View mode handler
  const handleViewModeChange = useCallback((mode: 'images' | 'list') => {
    setViewMode(mode);
    sessionStorage.setItem('profileHaveListViewMode', mode);
    setIsViewOpen(false);
  }, []);

  const { data, isLoading, isFetching, error, refetch } = useFetchPublicHaveListQuery({
    username,
    page,
    limit: 48,
    sort: sortOrder.sort,
    direction: sortOrder.direction,
    search: debouncedSearchTerm || undefined,
  });

  // Check if it's a 403 forbidden error (private have list)
  const isPrivate = error && typeof error === 'object' && 'status' in error && error.status === 403;

  // When loading more pages, accumulate previous results
  const handleLoadMore = () => {
    if (data?.haveList) {
      setPreviousPages((prev) => [...prev, ...data.haveList]);
    }
    setPage((p) => p + 1);
  };

  // Reset when username changes
  useEffect(() => {
    setPage(1);
    setPreviousPages([]);
    setSearchTerm('');
    setDebouncedSearchTerm('');
  }, [username]);

  // "Add to Trade" on another user's have list seeds the offer builder with
  // that card in the They send column. The price must be the same one the
  // offer will lock (quotedPricePoints from offer-candidates) — displayPrice
  // uses a different formula and must never seed the builder.
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { data: profileData } = useGetPublicProfileQuery(username, {
    skip: isOwnProfile || !currentUser,
  });
  const [fetchReceiveCandidates] = useLazyGetReceiveCandidatesQuery();
  const [isSeedingOffer, setIsSeedingOffer] = useState(false);

  // FAIL OPEN. Only an explicit `false` hides the affordance — `undefined` (a
  // cached payload, or this client running ahead of the API deploy) must leave
  // it visible. The failure modes are not symmetric: hiding it wrongly costs a
  // trade the user could have made, while showing it wrongly costs one clean
  // server rejection, which the enable-in-place dialog already handles. The
  // server is the authority either way; this is a courtesy, not the gate.
  const ownerOptedOutOfOffers = profileData?.allowIncomingTradeOffers === false;

  /**
   * The card whose Add to Trade was rejected because the VIEWER has offers off.
   * Holding the item (not a bare flag) is what lets the enable-in-place dialog
   * continue the original action with its context intact.
   */
  const [pendingOfferItem, setPendingOfferItem] = useState<CollectionList | null>(null);

  const handleAddToTrade = useCallback(
    async (item: CollectionList) => {
      if (!currentUser) {
        setShowLoginModal(true);
        return;
      }
      if (!profileData?.id || isSeedingOffer) return;
      const cardName = item.card?.name || 'Unknown Card';
      setIsSeedingOffer(true);
      try {
        // This tab only renders when the have list is public, so full mode is
        // available; resolve the exact have row to get its parity price.
        const result = await fetchReceiveCandidates(
          { userId: profileData.id, mode: 'full', search: cardName, limit: 50 },
          /* preferCacheValue */ true
        ).unwrap();
        const row =
          result.candidates.find((r) => r.userMTGCardHaveId === item.id) ||
          result.candidates.find(
            (r) =>
              r.card.id === item.card?.id &&
              r.foil === !!item.foil &&
              r.condition === (item.condition || 'NEAR_MINT')
          );
        if (!row) {
          toast.error(`${cardName} isn't available for trade offers right now.`);
          return;
        }
        if (row.quotedPricePoints == null) {
          toast.error(`${cardName} has no price data yet, so it can't be added to an offer.`);
          return;
        }
        writeOfferBuilderSeed({
          counterparty: { id: profileData.id, username },
          youSend: [],
          theySend: [
            {
              userMTGCardHaveId: row.userMTGCardHaveId,
              quantity: 1,
              ownedQuantity: row.ownedQuantity,
              unitPricePoints: row.quotedPricePoints,
              foil: row.foil,
              condition: row.condition,
              card: row.card,
            },
          ],
        });
        navigate(`/trade-offers/new/${username}`);
      } catch (err) {
        const parsed = parseOfferError(err);
        if (parsed.kind === 'proposer_offers_disabled') {
          // The VIEWER has offers off (reciprocity), which is recoverable in
          // place. Store this exact invocation — it closes over `item`, so
          // continuing re-runs the same card on the same profile rather than
          // dropping the user into an empty builder.
          setPendingOfferItem(item);
          return;
        }
        toast.error(parsed.message);
      } finally {
        setIsSeedingOffer(false);
      }
    },
    [currentUser, profileData, isSeedingOffer, fetchReceiveCandidates, username, navigate]
  );

  // Handle private have list
  if (isPrivate) {
    return <PrivateHaveListMessage isOwnProfile={isOwnProfile} onEnabled={refetch} />;
  }

  if (isLoading) {
    return <HaveListSkeleton />;
  }

  // Combine previous pages with current page data
  const allItems = [...previousPages, ...(data?.haveList || [])];

  // Track debounce pending state
  const isSearchPending = searchTerm !== debouncedSearchTerm;

  const totalCards = data?.totalCards || 0;
  const totalValue = data?.totalValue || 0;
  const hasMore = data?.hasMore || false;

  // Generate table cells for list view
  const tableCellsData: CardsTableCellsData = (() => {
    if (viewMode !== 'list') return { headCells: [], bodyRows: [] };

    const headCells: { key: string; label: string; columnWidth?: number; noSort?: boolean }[] = [
      { key: 'card_name', label: 'Name' },
      { key: 'setName', label: 'Set', columnWidth: 80, noSort: true },
    ];

    if (!isMobile) {
      headCells.push(
        { key: 'condition', label: 'Cond', columnWidth: 60, noSort: true },
        { key: 'isFoil', label: 'Foil', columnWidth: 50, noSort: true },
        { key: 'reference_price', label: 'Price', columnWidth: 80, noSort: true },
      );
    }

    headCells.push({ key: 'quantity', label: 'Qty', columnWidth: 50, noSort: true });

    return {
      headCells,
      bodyRows: allItems.map((row) => {
        const components: React.ReactNode[] = [
          // Name with thumbnail
          <div className="collection-card-name" key="name">
            {row.card?.scryfallID && (
              <img
                src={AssetUrls.cards.getCardImage(row.card.scryfallID, 'art_crop')}
                className="collection-card-name-img"
                alt={row.cardName || row.card?.name || 'Card'}
                onError={(e) => {
                  const img = e.currentTarget;
                  const scryfallId = row.card?.scryfallID;
                  if (scryfallId && !img.dataset.formatFallbackComplete) {
                    const currentSrc = img.src;
                    if (currentSrc.includes('/art_crop/')) {
                      img.src = AssetUrls.cards.getCardImage(scryfallId, 'normal');
                      return;
                    } else if (currentSrc.includes('/normal/')) {
                      img.src = AssetUrls.cards.getCardImage(scryfallId, 'small');
                      return;
                    } else if (currentSrc.includes('/small/')) {
                      img.src = AssetUrls.cards.getCardImage(scryfallId, 'large');
                      return;
                    }
                    img.dataset.formatFallbackComplete = 'true';
                  }
                  img.src = AssetUrls.cards.defaultBack;
                }}
              />
            )}
            <span className="collection-card-name-text">
              {row.cardName || row.card?.name}
            </span>
          </div>,
          // Set with icon + code
          <div className="collection-card-set" key="set">
            {row.card?.setIconUrl ? (
              <>
                <SetIcon
                  setIconUrl={row.card.setIconUrl}
                  setName={row.card?.setName || ''}
                  inverted={false}
                  size="sm"
                />
                <span className="collection-card-set-code">
                  {(row.card?.setCode || '').toUpperCase()}
                </span>
              </>
            ) : (
              <span className="collection-card-set-code">
                {(row.card?.setCode || '').toUpperCase()}
              </span>
            )}
          </div>,
        ];

        if (!isMobile) {
          components.push(
            // Condition
            <div key="cond">{row.condition ? (CONDITION_MAP[row.condition] || row.condition) : '-'}</div>,
            // Foil
            <div key="foil">
              {row.foil ? (
                <img
                  src={`${AssetUrls.base}/assets/add-cards/foil.png`}
                  width={14.15}
                  height={14.21}
                  alt="foil-icon"
                />
              ) : '-'}
            </div>,
            // Price
            <div key="price">
              {row.displayPrice != null
                ? <Points points={row.displayPrice} type="small" />
                : '???'}
            </div>,
          );
        }

        // Quantity
        components.push(
          <div key="qty"><span>{row.quantity}</span></div>,
        );

        return {
          key: row.id.toString(),
          card: row.card || ({} as MTGCard),
          components,
          onClick: () => {
            if (row.card) {
              navigate(buildCardUrl(row.card));
            }
          },
        };
      }),
    };
  })();

  // Controls bar
  const renderControls = () => (
    <div className="profile-have-controls">
      <div className="profile-have-search">
        <TextInputField
          leftComponent={<FontAwesomeIcon icon="magnifying-glass" color="#DD5BEA" size="sm" />}
          value={searchTerm}
          placeholder="Card Name"
          onChange={setSearchTerm}
        />
      </div>

      {/* View mode dropdown (desktop) */}
      <div className="profile-have-view-selector profile-have-desktop-only" ref={viewRef}>
        <span className="profile-have-dropdown-label">View</span>
        <button
          type="button"
          className={`profile-have-dropdown-trigger ${isViewOpen ? 'open' : ''}`}
          onClick={() => setIsViewOpen(!isViewOpen)}
          aria-haspopup="listbox"
          aria-expanded={isViewOpen}
          aria-label="Select view mode"
        >
          <img
            src={VIEW_OPTIONS.find(o => o.value === viewMode)?.icon}
            alt=""
            className="profile-have-dropdown-icon"
          />
          <span className="profile-have-dropdown-text">
            {VIEW_OPTIONS.find(o => o.value === viewMode)?.label}
          </span>
          <ChevronSvg open={isViewOpen} />
        </button>
        {isViewOpen && (
          <div className="profile-have-dropdown-menu" role="listbox">
            {VIEW_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`profile-have-dropdown-option ${viewMode === option.value ? 'selected' : ''}`}
                onClick={() => handleViewModeChange(option.value)}
                role="option"
                aria-selected={viewMode === option.value}
              >
                <img src={option.icon} alt="" className="profile-have-dropdown-option-icon" />
                <span>{option.label}</span>
              </button>
            ))}
            {viewMode === 'images' && (
              <>
                <div className="profile-have-dropdown-divider" />
                <div className="profile-have-size-toggle">
                  <span className="profile-have-size-label">Size</span>
                  <CardSizeSelector value={imageSize} onChange={handleImageSizeChange} stopPropagation />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Sort dropdown (desktop) */}
      <div className="profile-have-sort-selector profile-have-desktop-only" ref={sortRef}>
        <span className="profile-have-dropdown-label">Sort</span>
        <button
          type="button"
          className={`profile-have-dropdown-trigger ${isSortOpen ? 'open' : ''}`}
          onClick={() => setIsSortOpen(!isSortOpen)}
          aria-haspopup="listbox"
          aria-expanded={isSortOpen}
          aria-label="Select sort order"
        >
          <img src={AssetUrls.icons.controls.sort} alt="" className="profile-have-dropdown-icon" />
          <span className="profile-have-dropdown-text">{currentSortLabel}</span>
          <ChevronSvg open={isSortOpen} />
        </button>
        {isSortOpen && (
          <div className="profile-have-dropdown-menu" role="listbox">
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`profile-have-dropdown-option ${currentSortValue === option.value ? 'selected' : ''}`}
                onClick={() => handleSortSelect(option.value)}
                role="option"
                aria-selected={currentSortValue === option.value}
              >
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

    </div>
  );

  // Determine empty states
  const isEmpty = allItems.length === 0;
  const showSearchEmptyState = isEmpty && debouncedSearchTerm && !isSearchPending && !isFetching;
  const showGenericEmptyState = isEmpty && !debouncedSearchTerm && !isSearchPending && !isFetching;
  const showLoadingState = isEmpty && (isSearchPending || isFetching);

  return (
    <div className="profile-have-tab">
      <div className="profile-have-header">
        <span className="profile-have-count">{formatNumber(totalCards)} Cards</span>
        {totalValue > 0 && <Points points={totalValue} />}
      </div>

      {renderControls()}

      {showLoadingState && (
        <div className={`profile-have-grid size-${imageSize.toLowerCase()}`}>
          {[...Array(8)].map((_, i) => (
            <div key={i} className="profile-have-card-item">
              <Skeleton
                variant="rectangular"
                width="100%"
                sx={{ backgroundColor: 'rgba(255,255,255,0.1)', aspectRatio: '488 / 680', borderRadius: '8px' }}
              />
            </div>
          ))}
        </div>
      )}

      {showSearchEmptyState && (
        <div className="profile-have-no-results">
          <p>No cards matching &ldquo;{debouncedSearchTerm}&rdquo;</p>
        </div>
      )}

      {showGenericEmptyState && (
        <div className="profile-have-empty">
          {/* Two different situations, two different sentences: filtered views
              return unfilteredTotalCards, so "owns cards, none marked for
              trade" is never rendered as an empty collection (which would be
              false) and never as an error. Self-view is unfiltered, so the
              first branch can't fire there. */}
          {!isOwnProfile && (data?.unfilteredTotalCards ?? 0) > 0 ? (
            <p>{username} hasn&apos;t made any cards available for trade yet.</p>
          ) : (
            <p>This user&apos;s have list is empty.</p>
          )}
        </div>
      )}

      {!isEmpty && viewMode === 'images' && (
        <div className={`profile-have-grid size-${imageSize.toLowerCase()}`}>
          {allItems.map((item) => (
            <HaveListCardItem
              key={item.id}
              item={item}
              isOwnProfile={!!isOwnProfile}
              offersDisabled={ownerOptedOutOfOffers}
              onAddToTrade={
                isOwnProfile || ownerOptedOutOfOffers ? undefined : handleAddToTrade
              }
            />
          ))}
        </div>
      )}

      {!isEmpty && viewMode === 'list' && (
        <div className="profile-have-list-view">
          <CardsTable
            isLoading={false}
            cellsData={tableCellsData}
          />
        </div>
      )}

      {hasMore && (
        <div className="profile-have-load-more">
          <MagicButton
            label={isFetching ? 'Loading...' : 'Load More'}
            color="dark-grey"
            onPress={handleLoadMore}
            isLoading={isFetching}
            isDisabled={isFetching}
          />
        </div>
      )}

      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        collectionOwnerUsername={username}
      />

      <EnableTradeOffersModal
        isOpen={pendingOfferItem !== null}
        onClose={() => setPendingOfferItem(null)}
        onContinue={() => {
          const item = pendingOfferItem;
          setPendingOfferItem(null);
          // Re-runs the ORIGINAL click for the same card, so the user lands in
          // a builder seeded exactly as it would have been.
          return item ? handleAddToTrade(item) : undefined;
        }}
      />
    </div>
  );
};

export default ProfileHaveListTab;
